import { $getSelection, $isRangeSelection } from 'lexical';
import { arity, BUILTINS, FormulaFunctionName, paramAt, ParamSpec } from '../../formula-engine';
import { $displayOffsetOf, $serializeFormula } from './serialization';

export interface EnclosingCall {
  fn: FormulaFunctionName;
  /** 0-based index of the argument the caret is in. */
  argIndex: number;
  /** The completed arguments before the caret's, trimmed. */
  args: string[];
}

/**
 * The function calls a position is inside of, innermost first, from display
 * text alone: skips [bracket] and "string" spans (Excel's "" escape included),
 * tracks paren depth and commas. Never parses, so partial input (unclosed
 * bracket or string, missing ")") still yields the context. Unknown names and
 * bare parentheses are transparent.
 */
export function findEnclosingCalls(text: string, caret: number): EnclosingCall[] {
  const stack: { name: string; argStart: number; args: string[] }[] = [];
  let i = 0;
  while (i < caret) {
    const ch = text[i];
    if (ch === '[') {
      const close = text.indexOf(']', i + 1);
      i = close === -1 ? caret : close + 1;
      continue;
    }
    if (ch === '"') {
      let j = i + 1;
      while (j < text.length) {
        if (text[j] === '"') {
          if (text[j + 1] === '"') {
            j += 2; // escaped quote
            continue;
          }
          break;
        }
        j++;
      }
      i = j >= text.length ? caret : j + 1;
      continue;
    }
    if (ch === '(') {
      const name = /([A-Za-z_][A-Za-z0-9_]*)\s*$/.exec(text.slice(0, i))?.[1] ?? '';
      stack.push({ name, argStart: i + 1, args: [] });
    } else if (ch === ')') {
      stack.pop();
    } else if (ch === ',' && stack.length) {
      const top = stack[stack.length - 1];
      top.args.push(text.slice(top.argStart, i).trim());
      top.argStart = i + 1;
    }
    i++;
  }
  const calls: EnclosingCall[] = [];
  for (let depth = stack.length - 1; depth >= 0; depth--) {
    const { name, args } = stack[depth];
    const fn = name.toUpperCase();
    if (BUILTINS.has(fn)) {
      calls.push({ fn, argIndex: args.length, args });
    }
  }
  return calls;
}

/**
 * The enclosing calls at the collapsed caret, or [] without one. Call inside
 * editor.read/update — shared by the typeahead menus for context filtering.
 */
export function $enclosingCallsAtCaret(): EnclosingCall[] {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
    return [];
  }
  const caret = $displayOffsetOf(selection.anchor.getNode(), selection.anchor.offset);
  return findEnclosingCalls($serializeFormula().display, caret);
}

/** The registry param the caret's argument lands on, if the call takes one there. */
function paramOf(call: EnclosingCall | undefined): ParamSpec | undefined {
  const spec = call && BUILTINS.get(call.fn);
  return spec ? paramAt(spec, call.argIndex) : undefined;
}

/** The argument text of the nearest param of `kind` before the caret's argument. */
function precedingArg(call: EnclosingCall, kind: ParamSpec['kind']): string | undefined {
  const spec = BUILTINS.get(call.fn);
  if (!spec) {
    return undefined;
  }
  for (let i = call.argIndex - 1; i >= 0; i--) {
    if (paramAt(spec, i)?.kind === kind) {
      return call.args[i];
    }
  }
  return undefined;
}

/**
 * True when the position is a call argument that takes a list field:
 * REDUCE's second argument, SIZE's argument, or INDEX's or SUM's first.
 */
export function isListSlot(call: EnclosingCall | undefined): boolean {
  return paramOf(call)?.kind === 'list';
}

/**
 * True when the position is a call argument that takes a member of the call's
 * own list: INDEX's third argument or SUM's second. Like a list slot, only a
 * field chip parses there.
 */
export function isMemberSlot(call: EnclosingCall | undefined): boolean {
  return paramOf(call)?.kind === 'member';
}

/**
 * The list whose members a member slot may name, resolved from the call's own
 * list argument. Undefined outside a member slot, or when that argument is
 * not a resolvable list.
 */
export function memberSlotList(
  call: EnclosingCall | undefined,
  resolveListPath: (ref: string) => string | undefined
): string | undefined {
  if (!call || !isMemberSlot(call)) {
    return undefined;
  }
  const listRef = /^\[(.*)\]$/.exec(precedingArg(call, 'list') ?? '')?.[1];
  return listRef === undefined ? undefined : resolveListPath(listRef);
}

/** True when the position is KPI's first argument: only KPI names belong there. */
export function isKpiNameSlot(call: EnclosingCall | undefined): boolean {
  return paramOf(call)?.kind === 'kpi';
}

/** True when the position is KPI's second argument: a period token ([Month]/[Quarter]/[Year]). */
export function isKpiPeriodSlot(call: EnclosingCall | undefined): boolean {
  const param = paramOf(call);
  return param?.kind === 'token' && param.name === 'period';
}

/** True when the position is KPI's third argument: a scenario token ([Actual]/[Budget]). */
export function isKpiScenarioSlot(call: EnclosingCall | undefined): boolean {
  const param = paramOf(call);
  return param?.kind === 'token' && param.name === 'scenario';
}

/** Any KPI argument: like list/member slots, only a chip parses there. */
export function isKpiArgSlot(call: EnclosingCall | undefined): boolean {
  const kind = paramOf(call)?.kind;
  return kind === 'kpi' || kind === 'token';
}

/** What can occupy an argument position. */
export type SlotKind =
  /** Only a field chip parses here: list, member and KPI slots. */
  | 'field'
  /** Only a LAMBDA parses here: REDUCE's last argument. */
  | 'lambda'
  /** Any expression. */
  | 'free';

/**
 * How narrow a slot is — the one question autocomplete asks before jumping
 * ahead, since only a single-answer slot can be completed without guessing
 * (see functionInsertText, advanceAfterChip and commaCompletion). Past the
 * last argument nothing is forced.
 */
export function slotKind(call: EnclosingCall | undefined): SlotKind {
  const spec = call && BUILTINS.get(call.fn);
  if (!call || !spec || call.argIndex >= arity(spec).max) {
    return 'free';
  }
  switch (paramAt(spec, call.argIndex)?.kind) {
    case 'list':
    case 'member':
    case 'kpi':
    case 'token':
      return 'field';
    case 'lambda':
      return 'lambda';
    default:
      return 'free';
  }
}

/**
 * What starts a slot of each narrow kind, so the two paths that jump into one
 * — advanceAfterChip and commaCompletion — always insert the same thing. "["
 * hands over to the field typeahead; "LAMBDA(" is the only call REDUCE's last
 * argument takes.
 */
export const SLOT_OPENER: Record<Exclude<SlotKind, 'free'>, string> = {
  field: '[',
  lambda: 'LAMBDA(',
};

/**
 * The KPI a period/scenario slot belongs to, resolved from the call's own
 * KPI argument (the sibling-argument pattern memberSlotList uses).
 * Undefined outside those slots, or when that argument is not a
 * resolvable KPI — the caller then offers every token and lets validation
 * explain.
 */
export function kpiSlotKpi<T>(
  call: EnclosingCall | undefined,
  resolveKpi: (ref: string) => T | undefined
): T | undefined {
  if (!call || paramOf(call)?.kind !== 'token') {
    return undefined;
  }
  const ref = /^\[(.*)\]$/.exec(precedingArg(call, 'kpi') ?? '')?.[1];
  return ref === undefined ? undefined : resolveKpi(ref);
}

/**
 * The lists whose items are in scope for these enclosing calls: every
 * REDUCE(…, [List], LAMBDA(…)) body the position is inside of, innermost
 * first. `resolveListPath` maps the list argument's bracket text (display
 * name or path) to the list's canonical path, or undefined when it is not a
 * list.
 */
export function listsInScope(
  calls: EnclosingCall[],
  resolveListPath: (ref: string) => string | undefined
): string[] {
  const lists: string[] = [];
  for (const call of calls) {
    if (paramOf(call)?.kind !== 'lambda') {
      continue;
    }
    const listRef = /^\[(.*)\]$/.exec(precedingArg(call, 'list') ?? '')?.[1];
    const path = listRef === undefined ? undefined : resolveListPath(listRef);
    if (path !== undefined) {
      lists.push(path);
    }
  }
  return lists;
}

/** A bare name in scope at the caret, and which binder declared it. */
export interface BoundName {
  name: string;
  binder: 'LET' | 'LAMBDA';
}

const BARE_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * The names a position may use, innermost first, from the enclosing calls
 * alone (the name-scope twin of listsInScope): a LAMBDA's accumulator inside
 * its body, and a LET's names inside the arguments that follow them — a
 * value sees the names before its own; an even argument after the first pair
 * may be the calculation, so it sees every name before it (a name slot that
 * reuses one is the parser's "already used" error). A name that shadows an
 * outer one wins, as it does in evaluation.
 */
export function namesInScope(calls: EnclosingCall[]): BoundName[] {
  const names: BoundName[] = [];
  const add = (name: string, binder: BoundName['binder']) => {
    if (BARE_NAME.test(name) && !names.some((bound) => bound.name.toUpperCase() === name.toUpperCase())) {
      names.push({ name, binder });
    }
  };
  for (const call of calls) {
    if (call.fn === 'LAMBDA' && call.argIndex === 1) {
      add(call.args[0], 'LAMBDA');
    }
    if (call.fn === 'LET' && call.argIndex >= 1) {
      const visible = call.argIndex % 2 === 1 ? call.argIndex - 1 : call.argIndex;
      // Innermost-first order within one LET too: the latest name shadows.
      for (let i = visible - 2; i >= 0; i -= 2) {
        add(call.args[i], 'LET');
      }
    }
  }
  return names;
}
